import React, { useState, useEffect } from 'react';
import { X, FileText, DollarSign, CheckCircle, AlertCircle, Truck, RefreshCw, Eye, CreditCard, Receipt } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  generateProformaInvoice,
  regenerateProformaInvoice,
  markOrderAsCredit,
  generateLoadedDocuments,
  confirmDocumentReview
} from '../../api/wholesale/orders';
import { generateInvoicePDF, getInvoiceByOrderId } from '../../api/wholesale/invoices';
import PaymentRecordingModal from './PaymentRecordingModal';
import ProformaInvoicePreviewModal from './ProformaInvoicePreviewModal';
import InvoicePreviewModal from './InvoicePreviewModal';
import ReceiptPreviewModal from './ReceiptPreviewModal';

/**
 * ProformaAndPaymentModal
 * Comprehensive modal for handling proforma invoice generation,
 * payment confirmation or credit marking, before proceeding to loading
 */
const ProformaAndPaymentModal = ({ isOpen, onClose, order, onSuccess, onProceedToLoading }) => {
  const [generatingProforma, setGeneratingProforma] = useState(false);
  const [markingCredit, setMarkingCredit] = useState(false);
  const [generatingReceipt, setGeneratingReceipt] = useState(false);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showProformaPreview, setShowProformaPreview] = useState(false);
  const [showInvoicePreview, setShowInvoicePreview] = useState(false);
  const [showReceiptPreview, setShowReceiptPreview] = useState(false);
  const [localOrder, setLocalOrder] = useState(order);
  const [creditNotes, setCreditNotes] = useState('');
  const [generatedInvoiceData, setGeneratedInvoiceData] = useState(null); // Store invoice data from API

  // Document review states
  const [documentReviewed, setDocumentReviewed] = useState(false);
  const [confirmedReview, setConfirmedReview] = useState(false);
  const [showConfirmationPrompt, setShowConfirmationPrompt] = useState(false);
  const [pendingDocumentType, setPendingDocumentType] = useState(null); // 'invoice' or 'receipt'

  // Update local order when prop changes
  useEffect(() => {
    if (order) {
      setLocalOrder(order);

      // Check if document review is already confirmed in database
      if (order.document_review_confirmed) {
        setDocumentReviewed(true);
        setConfirmedReview(true);
        setShowConfirmationPrompt(false);
      } else {
        // Reset states if not confirmed
        setDocumentReviewed(false);
        setConfirmedReview(false);
        setShowConfirmationPrompt(false);
      }
    }
  }, [order]);

  // ESC key handler to close modal
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && isOpen && !generatingProforma && !showPaymentModal && !showProformaPreview && !markingCredit) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, generatingProforma, showPaymentModal, showProformaPreview, markingCredit]);

  const handleClose = () => {
    if (!generatingProforma && !showPaymentModal && !showProformaPreview && !markingCredit && !generatingReceipt) {
      onClose();
    }
  };

  const handleGenerateProforma = async () => {
    setGeneratingProforma(true);
    try {
      const response = await generateProformaInvoice(localOrder.id);
      toast.success('Proforma invoice generated successfully');
      const updatedOrder = { ...localOrder, ...response.data.order };
      setLocalOrder(updatedOrder);
      if (onSuccess) onSuccess(updatedOrder);
      // Open preview modal after generation
      setShowProformaPreview(true);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to generate proforma invoice');
    } finally {
      setGeneratingProforma(false);
    }
  };

  const handleRegenerateProforma = async () => {
    if (!window.confirm('Regenerate proforma invoice? This will update the timestamp.')) return;
    setGeneratingProforma(true);
    try {
      const response = await regenerateProformaInvoice(localOrder.id);
      toast.success('Proforma invoice regenerated successfully');
      const updatedOrder = { ...localOrder, ...response.data.order };
      setLocalOrder(updatedOrder);
      if (onSuccess) onSuccess(updatedOrder);
      // Open preview modal after regeneration
      setShowProformaPreview(true);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to regenerate proforma invoice');
    } finally {
      setGeneratingProforma(false);
    }
  };

  const handleViewProforma = () => {
    if (!localOrder.proforma_invoice_number) {
      toast.error('Please generate a proforma invoice first');
      return;
    }
    setShowProformaPreview(true);
  };

  const handleProformaPreviewSuccess = () => {
    // Mark that proforma was sent
    const updatedOrder = { ...localOrder, proforma_sent_at: new Date().toISOString() };
    setLocalOrder(updatedOrder);
    if (onSuccess) onSuccess(updatedOrder);
  };

  const handlePaymentSuccess = async (updatedOrder) => {
    // Close the payment modal first
    setShowPaymentModal(false);

    const newOrder = updatedOrder || { ...localOrder, payment_recorded_at: new Date().toISOString(), payment_status: 'paid' };
    setLocalOrder(newOrder);

    // Refresh order data
    if (onSuccess) await onSuccess(newOrder);

    // Check if it's partial or full payment
    const isPartialPayment = newOrder.payment_status === 'partial';

    if (isPartialPayment) {
      // Partial payment - show invoice only (backend will not generate receipt for partial payments)
      setGeneratingInvoice(true);
      setPendingDocumentType('invoice');
      try {
        // Generate documents (backend generates invoice only for partial payments)
        const result = await generateLoadedDocuments(newOrder.id);
        toast.success('Invoice generated successfully');

        // Get order and invoice data from response
        const orderFromApi = result.data?.order || result.order;
        const invoiceData = result.data?.invoice || result.invoice;

        const finalOrder = { ...newOrder, ...orderFromApi };
        setLocalOrder(finalOrder);

        // Store invoice data for the preview modal
        if (invoiceData) {
          setGeneratedInvoiceData(invoiceData);
        }

        if (onSuccess) await onSuccess(finalOrder);
        // Show invoice preview for partial payment
        setShowInvoicePreview(true);
      } catch (err) {
        console.error('Error generating invoice:', err);
        toast.error('Payment recorded but invoice generation failed. You can generate it manually later.');
      } finally {
        setGeneratingInvoice(false);
      }
    } else {
      // Full payment - show receipt
      setGeneratingReceipt(true);
      setPendingDocumentType('receipt');
      try {
        const result = await generateLoadedDocuments(newOrder.id);
        toast.success('Receipt generated successfully');
        const finalOrder = { ...newOrder, ...result.data?.order };
        setLocalOrder(finalOrder);
        if (onSuccess) await onSuccess(finalOrder);
        // Show receipt preview for full payment
        setShowReceiptPreview(true);
      } catch (err) {
        console.error('Error auto-generating receipt:', err);
        toast.error('Payment recorded but receipt generation failed. You can generate it manually later.');
      } finally {
        setGeneratingReceipt(false);
      }
    }
  };

  const handleMarkAsCredit = async () => {
    if (!window.confirm('Mark this order as credit? This will record it as an outstanding payment and generate an invoice.')) return;

    setMarkingCredit(true);
    try {
      const response = await markOrderAsCredit(localOrder.id, creditNotes);
      toast.success('Order marked as credit successfully.');
      const updatedOrder = { ...localOrder, ...response.data?.order, payment_status: 'credit', payment_recorded_at: new Date().toISOString() };
      setLocalOrder(updatedOrder);
      if (onSuccess) await onSuccess(updatedOrder);

      // Generate and show invoice for credit order
      setGeneratingInvoice(true);
      setPendingDocumentType('invoice');
      try {
        // Try to get invoice_id from response, if not present fetch it
        let invoiceId = updatedOrder.invoice_id;

        if (!invoiceId && updatedOrder.invoice_number) {
          // Fetch invoice_id using order ID
          try {
            const invoiceResponse = await getInvoiceByOrderId(updatedOrder.id);
            invoiceId = invoiceResponse.data?.invoice?.id;
          } catch (fetchError) {
            console.error('Error fetching invoice by order ID:', fetchError);
          }
        }

        if (!invoiceId) {
          toast.warning('Invoice created but PDF preview unavailable. Check the Documents page.');
          return;
        }

        await generateInvoicePDF(invoiceId);
        toast.success('Invoice generated successfully.');
        // Update local order to reflect invoice generation
        if (onSuccess) await onSuccess(updatedOrder);
        // Show invoice preview modal
        setShowInvoicePreview(true);
      } catch (invoiceErr) {
        console.error('Error generating invoice:', invoiceErr);
        toast.error('Invoice created but PDF generation failed. You can view it in the documents page.');
      } finally {
        setGeneratingInvoice(false);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to mark order as credit');
    } finally {
      setMarkingCredit(false);
    }
  };

  const handleProceedToLoading = () => {
    if (!localOrder.payment_recorded_at && localOrder.payment_status !== 'credit' && localOrder.payment_status !== 'paid') {
      toast.error('Please record payment or mark as credit before proceeding to loading');
      return;
    }
    onProceedToLoading();
  };

  // Handle document review button click
  const handleReviewDocument = () => {
    const currentPaymentStatus = localOrder.payment_status || 'unpaid';
    // Show invoice for credit and partial payments, receipt for full payments
    if (currentPaymentStatus === 'credit' || currentPaymentStatus === 'partial' || pendingDocumentType === 'invoice') {
      setShowInvoicePreview(true);
    } else {
      setShowReceiptPreview(true);
    }
  };

  // Handle invoice preview modal close - mark as reviewed
  const handleInvoicePreviewClose = () => {
    setShowInvoicePreview(false);
    if (!documentReviewed) {
      setDocumentReviewed(true);
      setShowConfirmationPrompt(true);
      toast.info('Please confirm you have reviewed the invoice before proceeding');
    }
  };

  // Handle receipt preview modal close - mark as reviewed
  const handleReceiptPreviewClose = () => {
    setShowReceiptPreview(false);
    if (!documentReviewed) {
      setDocumentReviewed(true);
      setShowConfirmationPrompt(true);
      toast.info('Please confirm you have reviewed the receipt before proceeding');
    }
  };

  // Handle explicit confirmation
  const handleConfirmReview = async () => {
    try {
      await confirmDocumentReview(localOrder.id);
      setConfirmedReview(true);
      setShowConfirmationPrompt(false);
      toast.success(`${pendingDocumentType === 'invoice' ? 'Invoice' : 'Receipt'} review confirmed. You can now proceed to loading.`);
    } catch (error) {
      console.error('Error confirming review:', error);
      toast.error('Failed to confirm review. Please try again.');
    }
  };

  if (!isOpen || !localOrder) return null;

  const hasProforma = !!localOrder.proforma_invoice_number;
  const paymentStatus = localOrder.payment_status || 'unpaid';
  const hasPayment = paymentStatus === 'paid' || paymentStatus === 'credit' || paymentStatus === 'partial' || !!localOrder.payment_recorded_at;
  const canProceedToLoading = hasProforma && hasPayment && confirmedReview;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
            <div>
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <FileText className="w-6 h-6 text-violet-600" />
                Proforma Invoice & Payment
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Order #{localOrder.order_number} - {localOrder.distributor_business_name || 'Customer'}
              </p>
            </div>
            <button
              onClick={handleClose}
              disabled={generatingProforma || markingCredit || generatingReceipt}
              className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">

            {/* Info Banner */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-900">
                  Final Steps Before Loading
                </p>
                <p className="text-sm text-blue-700 mt-1">
                  Complete the following steps to prepare the order for loading:
                </p>
                <ol className="list-decimal list-inside text-sm text-blue-700 mt-2 space-y-1">
                  <li>Generate final proforma invoice</li>
                  <li>Record payment <strong>OR</strong> mark as credit</li>
                  <li>Proceed to loading</li>
                </ol>
              </div>
            </div>

            {/* Progress Checklist */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <h3 className="font-semibold text-gray-900 text-sm mb-3">Completion Status</h3>

              {/* Proforma Generated */}
              <div className="flex items-center gap-3">
                {hasProforma ? (
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                ) : (
                  <div className="w-5 h-5 rounded-full border-2 border-gray-300 flex-shrink-0" />
                )}
                <div className="flex-1">
                  <p className={`text-sm font-medium ${hasProforma ? 'text-green-700' : 'text-gray-700'}`}>
                    Proforma Invoice {hasProforma ? 'Generated' : 'Not Generated'}
                  </p>
                  {hasProforma && (
                    <div className="flex items-center gap-2 mt-0.5">
                      <p className="text-xs text-gray-500">
                        {localOrder.proforma_invoice_number}
                      </p>
                      <button
                        onClick={handleViewProforma}
                        className="text-xs text-violet-600 hover:text-violet-700 underline"
                      >
                        View & Send
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Status */}
              <div className="flex items-center gap-3">
                {hasPayment ? (
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                ) : (
                  <div className="w-5 h-5 rounded-full border-2 border-gray-300 flex-shrink-0" />
                )}
                <div className="flex-1">
                  <p className={`text-sm font-medium ${hasPayment ? 'text-green-700' : 'text-gray-700'}`}>
                    {paymentStatus === 'paid' ? 'Payment Confirmed' :
                     paymentStatus === 'credit' ? 'Marked as Credit' :
                     paymentStatus === 'partial' ? 'Partially Paid' :
                     'Payment Not Recorded'}
                  </p>
                  {hasPayment && localOrder.payment_recorded_at && (
                    <p className="text-xs text-gray-500 mt-0.5">
                      {paymentStatus === 'credit' || paymentStatus === 'partial'
                        ? 'Invoice generated'
                        : 'Receipt generated'}
                    </p>
                  )}
                </div>
              </div>

              {/* Document Review Status */}
              {hasPayment && (
                <div className="flex items-center gap-3">
                  {confirmedReview ? (
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  ) : (
                    <div className="w-5 h-5 rounded-full border-2 border-gray-300 flex-shrink-0" />
                  )}
                  <div className="flex-1">
                    <p className={`text-sm font-medium ${confirmedReview ? 'text-green-700' : 'text-gray-700'}`}>
                      Document Review {confirmedReview ? 'Confirmed' : 'Required'}
                    </p>
                    {documentReviewed && !confirmedReview && (
                      <p className="text-xs text-amber-600 mt-0.5">
                        Click "Confirm & Proceed" button to continue
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="space-y-4">

              {/* Step 1: Generate/View Proforma */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h4 className="font-semibold text-gray-900 text-sm">Step 1: Proforma Invoice</h4>
                    <p className="text-xs text-gray-600 mt-1">
                      {hasProforma ? 'View, send via WhatsApp, or regenerate' : 'Generate final proforma for customer'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {hasProforma && (
                      <>
                        <button
                          onClick={handleViewProforma}
                          className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                          View & Send
                        </button>
                        <button
                          onClick={handleRegenerateProforma}
                          disabled={generatingProforma}
                          className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <RefreshCw className={`w-4 h-4 ${generatingProforma ? 'animate-spin' : ''}`} />
                          {generatingProforma ? 'Regenerating...' : 'Regenerate'}
                        </button>
                      </>
                    )}
                    {!hasProforma && (
                      <button
                        onClick={handleGenerateProforma}
                        disabled={generatingProforma}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <FileText className="w-4 h-4" />
                        {generatingProforma ? 'Generating...' : 'Generate Proforma'}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Step 2: Payment Options */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 text-sm mb-3">Step 2: Payment Method</h4>
                
                {hasPayment ? (
                  <div className="space-y-3">
                    {/* Payment confirmed banner */}
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-green-800">
                          {paymentStatus === 'credit' ? 'Order is on Credit' :
                           paymentStatus === 'partial' ? 'Partial Payment Confirmed' : 'Payment Confirmed'}
                        </p>
                        <p className="text-xs text-green-700">
                          {paymentStatus === 'credit' || paymentStatus === 'partial'
                            ? 'Invoice generated.'
                            : 'Receipt generated.'}
                        </p>
                      </div>
                      <button
                        onClick={handleReviewDocument}
                        className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      >
                        {documentReviewed ? 'View Again' : 'Review Document'}
                      </button>
                    </div>

                    {/* Confirmation prompt */}
                    {showConfirmationPrompt && !confirmedReview && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-amber-800">Confirm Document Review</p>
                          <p className="text-xs text-amber-700 mt-1">
                            Please confirm you've reviewed the {pendingDocumentType === 'invoice' ? 'invoice' : 'receipt'} before proceeding.
                          </p>
                          <button
                            onClick={handleConfirmReview}
                            className="mt-2 px-4 py-2 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
                          >
                            Confirm & Proceed
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Review confirmed */}
                    {confirmedReview && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <p className="text-sm font-medium text-green-800">Document reviewed and confirmed</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* Option A: Record Payment */}
                    <div className="border-2 border-emerald-200 rounded-lg p-4 hover:border-emerald-400 transition-colors bg-emerald-50/50">
                      <div className="flex items-center gap-2 mb-2">
                        <DollarSign className="w-5 h-5 text-emerald-600" />
                        <h5 className="font-medium text-gray-900">Payment Confirmation</h5>
                      </div>
                      <p className="text-xs text-gray-600 mb-3">
                        Record cash, bank transfer, or other payment. Receipt is generated for full payments only.
                      </p>
                      <button
                        onClick={() => setShowPaymentModal(true)}
                        disabled={generatingReceipt}
                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
                      >
                        <Receipt className="w-4 h-4" />
                        {generatingReceipt ? 'Processing...' : 'Record Payment'}
                      </button>
                    </div>

                    {/* Option B: Mark as Credit */}
                    <div className="border-2 border-amber-200 rounded-lg p-4 hover:border-amber-400 transition-colors bg-amber-50/50">
                      <div className="flex items-center gap-2 mb-2">
                        <CreditCard className="w-5 h-5 text-amber-600" />
                        <h5 className="font-medium text-gray-900">On Credit</h5>
                      </div>
                      <p className="text-xs text-gray-600 mb-3">
                        Mark as outstanding payment. Invoice will be auto-generated.
                      </p>
                      <button
                        onClick={handleMarkAsCredit}
                        disabled={markingCredit}
                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50"
                      >
                        <FileText className="w-4 h-4" />
                        {markingCredit ? 'Processing...' : 'Mark as Credit'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Success Message */}
            {canProceedToLoading && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-green-900">
                    Ready to Proceed!
                  </p>
                  <p className="text-sm text-green-700 mt-1">
                    All steps completed. You can now mark this order as loading.
                  </p>
                </div>
              </div>
            )}

            {/* Footer Actions */}
            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={handleClose}
                disabled={generatingProforma || markingCredit || generatingReceipt}
                className="flex-1 px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {canProceedToLoading ? 'Close' : 'Cancel'}
              </button>
              <button
                onClick={handleProceedToLoading}
                disabled={!canProceedToLoading}
                className="flex-1 px-6 py-2.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                title={!canProceedToLoading ? 'Complete all steps first' : ''}
              >
                <Truck className="w-5 h-5" />
                <span>Proceed to Loading</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Proforma Invoice Preview Modal */}
      {showProformaPreview && (
        <ProformaInvoicePreviewModal
          order={localOrder}
          onClose={() => setShowProformaPreview(false)}
          onSuccess={handleProformaPreviewSuccess}
        />
      )}

      {/* Invoice Preview Modal (for credit and partial payment orders) */}
      {showInvoicePreview && (
        <InvoicePreviewModal
          order={localOrder}
          invoice={generatedInvoiceData}
          onClose={handleInvoicePreviewClose}
        />
      )}

      {/* Receipt Preview Modal (for paid orders) */}
      {showReceiptPreview && (
        <ReceiptPreviewModal
          order={localOrder}
          isOpen={showReceiptPreview}
          onClose={handleReceiptPreviewClose}
        />
      )}

      {/* Payment Recording Modal */}
      <PaymentRecordingModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onSuccess={handlePaymentSuccess}
        order={localOrder}
      />
    </>
  );
};

export default ProformaAndPaymentModal;
